class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.searchRequestId = 0;
    this.onWindowResize = () => {
      if (!this.isOpen) return;
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) this.scrollOptionIntoView(selected);
    };

    this.setupEventListeners();
  }

  connectedCallback() {
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('scroll', this.onWindowResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('scroll', this.onWindowResize);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    // The typing debounce can finish after the customer has left search.
    if (!this.contains(document.activeElement)) return;
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    // An explicit Clear action must also reset a keyboard-selected suggestion.
    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      this.searchRequestId += 1;
      this.abortController.abort();
      this.removeAttribute('results');
      this.close();
    });
  }

  onKeyup(event) {
    if (event.isComposing) return;
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        if (event.target === this.input && this.getQuery() === this.searchTerm && !this.hasAttribute('loading')) this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (
      event.code === 'Enter' &&
      event.target === this.input &&
      this.getQuery() === this.searchTerm &&
      !this.hasAttribute('loading') &&
      this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')
    ) {
      event.preventDefault();
    }
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      if (currentButtonText.split(previousTerm).length !== 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    // A changed query can temporarily leave only the loading state visible.
    if (!allVisibleElements.length) return;

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
    this.scrollOptionIntoView(activeElement);
  }

  scrollOptionIntoView(option) {
    const optionBounds = option.getBoundingClientRect();
    const resultsBounds = this.predictiveSearchResults.getBoundingClientRect();
    const resultsTop = resultsBounds.top + this.predictiveSearchResults.clientTop;
    const resultsBottom = resultsTop + this.predictiveSearchResults.clientHeight;
    if (optionBounds.top < resultsTop) {
      this.predictiveSearchResults.scrollTop += optionBounds.top - resultsTop;
    } else if (optionBounds.bottom > resultsBottom) {
      this.predictiveSearchResults.scrollTop += optionBounds.bottom - resultsBottom;
    }
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    const requestId = (this.searchRequestId || 0) + 1;
    this.searchRequestId = requestId;
    this.abortController.abort();
    this.abortController = new AbortController();
    const isCurrent = () =>
      this.searchRequestId === requestId && this.searchTerm === searchTerm && this.getQuery() === searchTerm;
    this.setLiveRegionLoadingState();

    if (Object.prototype.hasOwnProperty.call(this.cachedResults, queryKey)) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    const requestController = this.abortController;
    const timeoutId = setTimeout(() => {
      if (!isCurrent()) return;
      // Invalidate late responses even if the transport does not honor abort.
      this.searchRequestId += 1;
      requestController.abort();
      this.removeAttribute('results');
      this.close();
    }, 30000);

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!isCurrent()) return null;
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (text === null || !isCurrent()) {
          searchDeferred?.resolve({ totalCount: 0 });
          return;
        }
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (!isCurrent() || error?.name === 'AbortError' || error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.removeAttribute('results');
        this.close();
      })
      .finally(() => clearTimeout(timeoutId));
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.input.removeAttribute('aria-activedescendant');
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    // Cached markup is shared, but each search field owns its result identities.
    const resultIds = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
      const originalId = element.id;
      element.id = `${this.input.id}-${originalId}`;
      resultIds.set(originalId, element.id);
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby], [aria-describedby]').forEach((element) => {
      for (const attribute of ['aria-labelledby', 'aria-describedby']) {
        const value = element.getAttribute(attribute);
        if (value) element.setAttribute(attribute, value.split(/\s+/).map((id) => resultIds.get(id) || id).join(' '));
      }
    });
    this.predictiveSearchResults.querySelectorAll('[role="option"]').forEach((element, index) => {
      if (!element.id) element.id = `${this.input.id}-option-${index}`;
    });
    const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
    if (listbox) {
      this.input.setAttribute('aria-controls', listbox.id);
      this.input.setAttribute('aria-owns', listbox.id);
    }
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const resultsTop = this.predictiveSearchResults.getBoundingClientRect().top;
    this.resultsMaxHeight = Math.max(0, window.innerHeight - Math.max(0, resultsTop));
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute('open', true);
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
